<template>
  <b-dropdown
    ref="dropdown"
    variant="light"
    size="sm"
    :right="right"
    :boundary="boundary"
    :toggle-class="toggleClass"
    :no-flip="noFlip"
    :disabled="disabled"
    @show="onShow"
    @shown="onShown"
    @hide="onHide"
    @hidden="onHidden"
    @mousedown.prevent.stop
  >
    <template #button-content>
      <span :class="(!selected && !is_none) || busy ? 'text-muted' : 'text-primary'">
        <i class="fa fa-fw fa-tag"></i>
        {{ buttonContent }}
      </span>
    </template>
    <b-dropdown-text v-if="busy">
      <small>
        <i class="fa fa-fw fa-circle-notch fa-spin"></i>
        {{ trans('please_wait') }}
      </small>
    </b-dropdown-text>
    <b-dropdown-form v-if="!busy && multiple" class="text-center" style="min-width: 300px;">
      <b-row>
        <b-col>
          <small v-html="trans('filter_hold_ctrl_multiple')"></small>
          <b-button variant="link" size="sm" v-if="canManageTags" v-b-modal.tags-settings-modal class="py-0">
            <i class="fa fa-fw fa-cog"></i> {{ trans('manage') }}
          </b-button>
        </b-col>
        <b-col v-if="allowMultipleMode">
          <b-form-radio-group size="sm" v-model="multiple_mode" :disabled="busy">
            <b-form-radio value="and">
              <span :title="trans('and_tags')">{{ trans('and').toUpperCase() }}</span>
            </b-form-radio>
            <b-form-radio value="or">
              <span :title="trans('or_tags')">{{ trans('or').toUpperCase() }}</span>
            </b-form-radio>
            <b-form-radio value="not">
              <span :title="trans('not_tags')">{{ trans('not').toUpperCase() }}</span>
            </b-form-radio>
          </b-form-radio-group>
        </b-col>
      </b-row>
    </b-dropdown-form>
    <div style="margin: 0 20px;">
      <b-form-input
        size="sm"
        v-if="!busy"
        v-model="search"
        :placeholder="trans('search')"/>
    </div>
    <b-dropdown-item v-if="!busy" @click="onSelectTag($event, null);" :disabled="busy">
      <i :class="`fa fa-fw ${isSelected(null) ? 'fa-check' : ''}`"></i>
      <em>{{ anyLabel }}</em>
    </b-dropdown-item>
    <b-dropdown-item v-if="!busy && allowNone" @click="onSelectTag($event, null, true)" :disabled="busy">
      <i :class="`fa fa-fw ${is_none ? 'fa-check' : ''}`"></i>
      <em>{{ noneLabel }}</em>
    </b-dropdown-item>
    <div style="max-height: 50vh; overflow-y: auto;">
      <b-dropdown-item v-if="!busy" v-for="tag in filteredTags" :key="tag.id" @click="onSelectTag($event, tag)">
        <i :class="`fa fa-fw ${isSelected(tag) ? 'fa-check' : ''}`"></i>
        {{ tag.name }}
      </b-dropdown-item>
    </div>
    <TagsSettingsModal
      v-if="canManageTags"
      :organization="organization"
      :type="type"
      :placeholder="placeholder"
      :label="label"
    />
  </b-dropdown>
</template>

<script>
import TagsSettingsModal from './TagsSettingsModal'

export default {
  props: {
    organization: Object,
    allTags: Array,
    type: String,
    multiple: {
      type: Boolean,
      default: false,
    },
    multipleNotLazy: {
      type: Boolean,
      default: false,
    },
    allowMultipleMode: {
      type: Boolean,
      default: false,
    },
    multipleMode: {
      type: String,
      default: 'and', // and, or, not
    },
    placeholder: {
      type: String,
      default: trans('tags'),
    },
    label: {
      type: String,
      default: 'tag',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    right: {
      type: Boolean,
      default: false,
    },
    boundary: {
      type: String,
      default: 'scrollParent',
    },
    value: String | Array,
    toggleClass: String,
    noFlip: {
      type: Boolean,
      default: false,
    },
    allowNone: {
      type: Boolean,
      default: false,
    },
    isNone: Boolean,
    noneLabel: {
      type: String,
      default: trans('no_tag'),
    },
    anyLabel: {
      type: String,
      default: trans('any'),
    },
    keepOpen: {
      type: Boolean,
      default: false,
    },
    showManage: {
      type: Boolean,
      default: false,
    },
    preload: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TagsSettingsModal,
  },
  data() {
    return {
      trans: trans,
      busy: false,
      error: null,
      all_tags: this.allTags,
      selected: _.isArray(this.value) ? _.clone(this.value) : this.value,
      is_none: !!this.isNone,
      multiple_mode: this.multipleMode,
      can_close: true,
      keep_open: false,
      search: null,
      loaded_for_type: null,
    }
  },
  computed: {
    buttonContent() {
      if (this.is_none) {
        return this.noneLabel
      }
      if (_.isArray(this.selected)) {
        if (this.selected.length == 1){
          return this.selected[0]
        }
        if (this.selected.length > 1) {
          return this.selected.length + ' ' + this.placeholder.toLowerCase() + (this.allowMultipleMode ? ` (${trans(this.multiple_mode).toUpperCase()})` : '')
        }
        return this.placeholder
      } else {
        return !!this.selected ? this.selected : this.placeholder
      }
    },
    canManageTags() {
      return this.showManage && this.organization.auth_user.can_manage_tags
    },
    filteredTags() {
      if (!this.search) return this.all_tags
      return _.filter(this.all_tags, tag => tag.name.toLowerCase().indexOf(this.search.toLowerCase()) >= 0)
    },
  },
  watch: {
    value() {
      if (!_.isEqual(this.value, this.selected))
        this.selected = _.isArray(this.value) ? _.clone(this.value) : this.value
    },
    allTags() {
      if (!_.isEqual(this.allTags, this.all_tags))
        this.all_tags = this.allTags
    },
    is_none() {
      if (this.is_none != this.isNone)
        this.$emit('is-none', this.is_none)
    },
    type() {
      if (!!this.loaded_for_type && this.loaded_for_type != this.type) this.loadAllTags()
    },
  },
  methods: {
    isSelected(tag) {
      if (!tag) {
        return (!this.selected || this.selected.length == 0) && !this.is_none
      }
      if (_.isArray(this.selected)) {
        return this.selected.indexOf(tag.name) >= 0
      }
      return this.selected == tag.name
    },
    onSelectTag(e, tag, is_none) {
      this.keep_open = false
      if (is_none) {
        this.is_none = true
        if (this.is_none != this.isNone)
          this.$emit('is-none', this.is_none)
        tag = null
        if (this.multiple) {
          this.selected = []
          this.maybeEmitMultiple()
          this.$emit('input', _.clone(this.selected))
          return
        }
      } else {
        if (this.is_none) this.is_none = false
        if (this.is_none != this.isNone)
          this.$emit('is-none', this.is_none)
        if (!tag && this.multiple) {
          this.selected = []
          this.maybeEmitMultiple()
          this.$emit('input', _.clone(this.selected))
          return
        }
      }
      if (this.multiple) {
        if (!tag && this.selected && this.selected.length > 0) {
          this.selected = []
          return
        }
        if (!e.ctrlKey && !e.metaKey) {
          this.selected = [tag.name]
        } else {
          this.can_close = false
          let selected = this.selected || []
          let index = selected.indexOf(tag.name)
          if (index >= 0) {
            selected.splice(index, 1)
          } else {
            selected.push(tag.name)
          }
          this.selected = selected
          if (this.multipleNotLazy && !_.isEqual(this.value, this.selected)) {
            this.can_close = true
            this.keep_open = true
            this.maybeEmitMultiple()
            this.$emit('input', _.clone(this.selected))
          }
        }
      } else {
        this.selected = tag ? tag.name : null
        this.$emit('input', this.selected)
      }
    },
    onShow() {
      if (this.keepOpen) return
      this.busy = false
      this.error = false
      if (!this.all_tags) this.loadAllTags()
      this.$emit('show')
    },
    onShown() {
      if (this.keepOpen) return
      this.$emit('shown')
    },
    maybeEmitMultiple() {
      if (this.allowMultipleMode && this.multiple_mode != this.multipleMode) {
        this.$emit('multiple-mode', this.multiple_mode)
      }
    },
    onHide(e) {
      if (this.keep_open) return
      if (this.can_close) {
        if (!_.isEqual(this.value, this.selected)) {
          this.maybeEmitMultiple()
          this.$emit('input', _.clone(this.selected))
        } else if (this.allowMultipleMode && this.multiple_mode != this.multipleMode) {
          this.$emit('multiple-mode', this.multiple_mode)
          this.$emit('input', _.clone(this.selected))
        }
      } else {
        e.preventDefault()
        this.can_close = true
      }
      if (this.search) {
        this.search = null
      }
      this.$emit('hide')
    },
    onHidden() {
      if (this.keep_open) return
      this.$emit('hidden')
    },
    loadAllTags() {
      this.busy = true
      this.error = null
      let url = `/organization/${this.organization.id}/tags`
      if (this.type) url += '?type=' + this.type
      axios.get(url)
        .then(response => {
          this.busy = false
          if (!!this.type) this.loaded_for_type = this.type
          this.all_tags = response.data
          this.$emit('all-tags', this.all_tags)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
    },
  },
  updated() {
    if (this.keepOpen && this.$refs.dropdown) this.$refs.dropdown.show()
  },
  mounted() {
    window.tagsFilter = this
    if (this.preload) this.loadAllTags()
  },
}
</script>
